import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button, LoadingSpinner, ErrorState } from '../../components/ui';
import { useApiQuery } from '../../hooks/useApi';
import { useFotoGuiaCaptura } from '../../hooks/useFotoGuiaCaptura';
import ordenesCargaCombustibleService from '../../services/ordenesCargaCombustibleService';
import { guardarBorradorCarga, limpiarBorradorCarga } from '../../utils/ordenCargaDraftStorage';

export const CargarOrdenMovilView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, destinoId } = useParams<{ id: string; destinoId: string }>();
  const ordenId = id ? parseInt(id, 10) : null;
  const destinoIdNum = destinoId ? parseInt(destinoId, 10) : null;

  const [cantidadCargada, setCantidadCargada] = useState('');
  const [rutCargador, setRutCargador] = useState('');
  const [nombreCargador, setNombreCargador] = useState('');
  const [horometroDeclarado, setHorometroDeclarado] = useState('');
  const [nivelDeclarado, setNivelDeclarado] = useState('');
  const [enviando, setEnviando] = useState(false);
  const fotoGuia = useFotoGuiaCaptura();
  // Foto opcional del horómetro + nivel de estanque (mismo concepto que la
  // foto de evidencia de Control de Combustible), independiente de la foto
  // de guía de despacho.
  const fotoNivel = useFotoGuiaCaptura();

  const { data: orden, isLoading } = useApiQuery(
    ['orden-carga', ordenId],
    () => ordenId ? ordenesCargaCombustibleService.obtener(ordenId) : Promise.resolve(null),
    { enabled: !!ordenId }
  );

  const destino = orden?.destinos.find((d) => d.id === destinoIdNum) || null;

  // Prefill del RUT y nombre con los del operador autorizado (editables):
  // normalmente es quien carga, pero puede corregirse si carga otra persona.
  useEffect(() => {
    if (orden?.operador?.rut && !rutCargador) {
      setRutCargador(orden.operador.rut);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orden?.operador?.rut]);

  useEffect(() => {
    const nombreOperador = orden?.operador?.nombre_completo || orden?.operador?.descripcion;
    if (nombreOperador && !nombreCargador) {
      setNombreCargador(nombreOperador);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orden?.operador?.nombre_completo, orden?.operador?.descripcion]);

  const destinoTexto = destino?.vehiculo
    ? `🚚 ${destino.vehiculo.codigo} - ${destino.vehiculo.patente}`
    : destino?.maquina
      ? `⚙️ ${destino.maquina.codigo} - ${destino.maquina.descripcion || ''}`
      : '-';

  const esValido = fotoGuia.listo && !!cantidadCargada && Number(cantidadCargada.replace(',', '.')) > 0 && rutCargador.trim() !== '' && nombreCargador.trim() !== '';

  const handleConfirmar = async () => {
    if (!ordenId || !destinoIdNum || !fotoGuia.fotoFile || !esValido) return;
    const cantidad = Number(cantidadCargada.replace(',', '.'));
    const horometro = horometroDeclarado ? Number(horometroDeclarado.replace(',', '.')) : undefined;
    const nivel = nivelDeclarado ? Number(nivelDeclarado.replace(',', '.')) : undefined;

    const rut = rutCargador.trim();
    const nombre = nombreCargador.trim();
    setEnviando(true);
    // Borrador local (incluida la foto en base64): si se pierde cobertura
    // justo al subir la evidencia, esto sobrevive para reintentar después.
    await guardarBorradorCarga(ordenId, destinoIdNum, cantidad, rut, nombre, fotoGuia.fotoFile, horometro, nivel, fotoNivel.fotoFile || undefined);
    try {
      await ordenesCargaCombustibleService.cargar(ordenId, destinoIdNum, {
        cantidadCargada: cantidad,
        rutCargador: rut,
        nombreCargador: nombre,
        fotoGuiaDespacho: fotoGuia.fotoFile,
        horometroDeclarado: horometro,
        nivelDeclarado: nivel,
        fotoEvidenciaNivel: fotoNivel.fotoFile || undefined,
      });
      limpiarBorradorCarga();
      toast.success(t('ordenes_carga.messages.cargada'));
      navigate('/ordenes-carga-combustible');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || error?.message || t('ordenes_carga.messages.error_cargar'));
    } finally {
      setEnviando(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!orden || orden.estado !== 'PENDIENTE' || !destino || destino.estado !== 'PENDIENTE') {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <ErrorState message={t('ordenes_carga.cargar.no_disponible')} />
        <div className="mt-4">
          <Button fullWidth onClick={() => navigate('/ordenes-carga-combustible')}>
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      {/* Header sticky */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10 flex items-center">
        <button onClick={() => navigate(-1)} className="mr-3 text-gray-600 hover:text-gray-900">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900">{t('ordenes_carga.cargar.title')}</h1>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-sm text-gray-700 space-y-1">
          <div className="font-semibold text-gray-900">{orden.numero_orden}</div>
          <div>{orden.proveedor?.nombre}</div>
          <div>{destinoTexto}</div>
          <div>{t('ordenes_carga.form.cantidad_autorizada')}: {destino.cantidad_autorizada} {orden.unidad}</div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ordenes_carga.cargar.cantidad_cargada')}
          </label>
          <input
            type="text"
            inputMode="decimal"
            placeholder="0.0"
            value={cantidadCargada}
            onChange={(e) => setCantidadCargada(e.target.value.replace(/[^0-9.,]/g, ''))}
            className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ordenes_carga.cargar.nombre_cargador')}
          </label>
          <input
            type="text"
            inputMode="text"
            placeholder={t('ordenes_carga.cargar.nombre_cargador_placeholder')}
            value={nombreCargador}
            onChange={(e) => setNombreCargador(e.target.value)}
            className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('ordenes_carga.cargar.rut_cargador')}
          </label>
          <input
            type="text"
            inputMode="text"
            placeholder={t('ordenes_carga.cargar.rut_cargador_placeholder')}
            value={rutCargador}
            onChange={(e) => setRutCargador(e.target.value)}
            className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ordenes_carga.cargar.horometro_declarado')}
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={horometroDeclarado}
              onChange={(e) => setHorometroDeclarado(e.target.value.replace(/[^0-9.,]/g, ''))}
              className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('ordenes_carga.cargar.nivel_declarado')}
            </label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0.0"
              value={nivelDeclarado}
              onChange={(e) => setNivelDeclarado(e.target.value.replace(/[^0-9.,]/g, ''))}
              className="block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('ordenes_carga.cargar.tomar_foto')}
          </label>
          <div className="flex flex-col items-center gap-3">
            {fotoGuia.fotoFile ? (
              <img
                src={URL.createObjectURL(fotoGuia.fotoFile)}
                alt="guía de despacho"
                className="w-full max-h-72 object-contain rounded border border-gray-300 bg-white"
              />
            ) : (
              <div className="w-full h-40 rounded bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-sm">
                {t('ordenes_carga.cargar.sin_foto')}
              </div>
            )}
            <Button type="button" variant="secondary" fullWidth onClick={fotoGuia.capturarFoto}>
              {fotoGuia.fotoFile ? t('ordenes_carga.cargar.retomar_foto') : t('ordenes_carga.cargar.tomar_foto')}
            </Button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('ordenes_carga.cargar.foto_nivel_label')} ({t('faenas.optional')})
          </label>
          <div className="flex flex-col items-center gap-3">
            {fotoNivel.fotoFile ? (
              <img
                src={URL.createObjectURL(fotoNivel.fotoFile)}
                alt="horómetro y nivel"
                className="w-full max-h-72 object-contain rounded border border-gray-300 bg-white"
              />
            ) : (
              <div className="w-full h-40 rounded bg-gray-100 border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-sm">
                {t('ordenes_carga.cargar.sin_foto')}
              </div>
            )}
            <Button type="button" variant="secondary" fullWidth onClick={fotoNivel.capturarFoto}>
              {fotoNivel.fotoFile ? t('ordenes_carga.cargar.retomar_foto') : t('ordenes_carga.cargar.tomar_foto_nivel')}
            </Button>
          </div>
        </div>
      </div>

      {/* Footer fijo, encima del MobileNavigation */}
      <div className="fixed bottom-20 left-0 right-0 bg-white border-t border-gray-200 p-4 z-[9999] flex gap-3 shadow-lg safe-area-bottom">
        <Button variant="secondary" fullWidth onClick={() => navigate(-1)} disabled={enviando}>
          {t('common.cancel')}
        </Button>
        <Button fullWidth onClick={handleConfirmar} disabled={!esValido} loading={enviando}>
          {t('ordenes_carga.cargar.confirmar')}
        </Button>
      </div>
    </div>
  );
};

export default CargarOrdenMovilView;
